import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../utils/api';
import useXHR from '../../hooks/useXHR';

const InlineCategoryManager = ({ showToast, setCurrentPage }) => {
  const { token } = useAuth();
  const { get, post, del } = useXHR(token);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editProtocol, setEditProtocol] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [editPrice, setEditPrice] = useState('');
  const [cleaningCategory, setCleaningCategory] = useState(false);
  const [categoryResultsCount, setCategoryResultsCount] = useState(null);

  const fetchCategories = useCallback(() => {
    setCategoriesLoading(true);
    get('/categories', {
      onSuccess: (data) => setCategories(data || []),
      onError: () => console.error('Failed to fetch categories'),
      onFinally: () => setCategoriesLoading(false)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showCategoryManager) fetchCategories();
  }, [showCategoryManager, fetchCategories]);

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setEditCategoryName(cat.name);
    setEditProtocol(cat.protocol || '');
    setEditIsPublic(cat.is_public || false);
    setEditPrice(cat.price || '');
    setCategoryResultsCount(null);
    get(`/categories/${cat.id}/results-count`, {
      onSuccess: (data) => setCategoryResultsCount(data)
    });
  };

  const cleanCategory = (categoryId, mode = 'remove') => {
    const modeMessages = {
      'remove': 'remove the category tag from all results',
      'delete': 'delete results ONLY in this category',
      'delete_all': 'DELETE ALL results associated with this category (cannot be undone!)'
    };
    const confirmMessage = `Are you sure you want to ${modeMessages[mode]}?\n\n` +
      (categoryResultsCount ? `This will affect ${categoryResultsCount.total_results} search results.` : '');
    if (!window.confirm(confirmMessage)) return;
    setCleaningCategory(true);
    post(`/categories/${categoryId}/clean?mode=${mode}`, {
      onSuccess: (data) => {
        showToast(data.message, 'success');
        get(`/categories/${categoryId}/results-count`, {
          onSuccess: (countData) => setCategoryResultsCount(countData)
        });
        fetchCategories();
      },
      onError: (err) => showToast(err || 'Failed to clean category', 'error'),
      onFinally: () => setCleaningCategory(false)
    });
  };

  const saveCategory = () => {
    if (!editingCategory) return;
    post(`/categories/${editingCategory.id}`, {
      method: 'PUT',
      body: { name: editCategoryName, protocol: editProtocol, is_public: editIsPublic, price: editPrice ? parseFloat(editPrice) : 0 },
      onSuccess: () => { showToast('Category updated!', 'success'); setEditingCategory(null); fetchCategories(); },
      onError: (err) => showToast(err || 'Failed to update category', 'error')
    });
  };

  const deleteCategory = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    const childCount = categories.filter(c => c.parent_id === categoryId).length;
    const confirmMsg = childCount > 0
      ? `Delete "${cat?.name}" and its ${childCount} sub-categor${childCount === 1 ? 'y' : 'ies'}? This cannot be undone!`
      : `Delete "${cat?.name}"? This cannot be undone!`;
    if (!window.confirm(confirmMsg)) return;
    del(`/categories/${categoryId}`, {
      onSuccess: () => { showToast('Category deleted!', 'success'); setEditingCategory(null); fetchCategories(); },
      onError: (err) => showToast(err || 'Failed to delete category', 'error')
    });
  };

  const getLevelLabel = (level) => {
    if (level === 0) return 'Category';
    if (level === 1) return 'Sub-category';
    if (level === 2) return 'Sub-sub-category';
    return `L${level} Sub-category`;
  };

  const levelColors = ['#FEF08A', '#047857', '#3b82f6', '#10b981', '#f59e0b'];

  const buildCategoryTree = (cats, parentId = null, level = 0) => {
    return cats.filter(cat => cat.parent_id === parentId).map(cat => {
      const childCount = cats.filter(c => c.parent_id === cat.id).length;
      const levelColor = levelColors[Math.min(level, levelColors.length - 1)];
      return (
        <div key={cat.id}>
          <div style={{
            padding: '10px 12px', marginLeft: level * 20, marginBottom: 6,
            background: editingCategory?.id === cat.id ? 'rgba(252, 211, 77,0.2)' : 'rgba(255, 255, 255,0.4)',
            borderRadius: 8,
            border: editingCategory?.id === cat.id ? '1px solid rgba(252, 211, 77,0.5)' : '1px solid rgba(255, 255, 255, 0.85)',
            borderLeft: `3px solid ${levelColor}`, cursor: 'pointer'
          }} onClick={() => handleEditCategory(cat)} data-testid={`settings-category-${cat.id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: cat.is_public ? '#10b981' : '#e2e8f0', fontWeight: 500 }}>
                  {level > 0 && <span style={{ color: levelColor, marginRight: 5 }}>{'\u2514'}</span>}
                  {cat.name}
                </span>
                {childCount > 0 && (
                  <span style={{ fontSize: '0.7rem', color: '#047857', background: 'rgba(252, 211, 77,0.2)', padding: '2px 6px', borderRadius: 4 }}>
                    {childCount} sub
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {cat.is_public && <span style={{ fontSize: '0.65rem', color: '#10b981', padding: '2px 6px', background: 'rgba(16,185,129,0.2)', borderRadius: 4 }}>Public</span>}
                <button onClick={(e) => { e.stopPropagation(); cleanCategory(cat.id, 'delete_all'); }}
                  style={{ background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', padding: '4px 8px', borderRadius: 4, fontSize: '0.7rem', cursor: 'pointer' }}
                  title="Clean all search results" data-testid={`clean-category-quick-${cat.id}`}>
                  <i className="fas fa-broom" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id); }}
                  style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '4px 8px', borderRadius: 4, fontSize: '0.7rem', cursor: 'pointer' }}
                  data-testid={`delete-category-settings-${cat.id}`}>
                  <i className="fas fa-trash" />
                </button>
              </div>
            </div>
            {cat.protocol && (
              <div style={{ fontSize: '0.7rem', color: '#71717a', marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {cat.protocol.length > 50 ? cat.protocol.substring(0, 50) + '...' : cat.protocol}
              </div>
            )}
          </div>
          {buildCategoryTree(cats, cat.id, level + 1)}
        </div>
      );
    });
  };

  return (
    <div style={{ padding: 20, background: '#475569', borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showCategoryManager ? 15 : 0 }}>
        <h3 style={{ color: '#047857', margin: 0 }}><i className="fas fa-folder-open" style={{ marginRight: 8 }} /> Category Management</h3>
        <button className="btn btn-secondary" onClick={() => setShowCategoryManager(!showCategoryManager)}
          style={{ padding: '8px 16px', fontSize: '0.9rem' }} data-testid="toggle-category-manager-btn">
          {showCategoryManager ? 'Hide' : 'Manage Categories'}
        </button>
      </div>
      
      {!showCategoryManager && (
        <p style={{ fontSize: '0.85rem', color: '#a1a1aa', marginTop: 10 }}>
          View, edit, and delete your categories and sub-categories here.
        </p>
      )}
      
      {showCategoryManager && (
        <div>
          {categoriesLoading ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#a1a1aa' }}>Loading categories...</div>
          ) : categories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <p style={{ color: '#a1a1aa' }}>No categories yet.</p>
              <button className="btn btn-primary" onClick={() => setCurrentPage('ultimate-search')} style={{ marginTop: 10 }}>
                Go to Ultimate Search to create categories
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: editingCategory ? '1fr 1fr' : '1fr', gap: 20 }}>
              <div style={{ maxHeight: 400, overflowY: 'auto', padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 8 }}>
                <p style={{ fontSize: '0.75rem', color: '#a1a1aa', marginBottom: 10 }}>Click any category to edit &middot; {categories.length} total</p>
                {buildCategoryTree(categories)}
              </div>
              
              {editingCategory && (
                <div style={{ padding: 15, background: 'rgba(252, 211, 77,0.1)', borderRadius: 10, border: '1px solid rgba(252, 211, 77,0.3)' }}>
                  <h4 style={{ color: '#047857', margin: '0 0 5px 0' }}>
                    <i className="fas fa-edit" style={{ marginRight: 6 }} /> Edit: {editingCategory.name}
                  </h4>
                  <p style={{ color: '#047857', fontSize: '0.75rem', margin: '0 0 15px 0' }}>
                    {getLevelLabel(editingCategory.level || 0)}
                    {editingCategory.parent_id && (() => {
                      const parent = categories.find(c => c.id === editingCategory.parent_id);
                      return parent ? ` under "${parent.name}"` : '';
                    })()}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={{ color: '#047857', fontSize: '0.85rem', display: 'block', marginBottom: 5 }}>Category Name</label>
                      <input type="text" value={editCategoryName} onChange={(e) => setEditCategoryName(e.target.value)}
                        className="input" placeholder="Category name" data-testid="edit-category-name-settings" />
                    </div>
                    <div>
                      <label style={{ color: '#047857', fontSize: '0.85rem', display: 'block', marginBottom: 5 }}>Protocol</label>
                      <textarea value={editProtocol} onChange={(e) => setEditProtocol(e.target.value)}
                        placeholder="(term1 or term2) & (term3 or term4)" rows={3}
                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid rgba(252, 211, 77,0.3)', background: '#475569', color: '#0F172A', fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                        data-testid="edit-protocol-settings" />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" checked={editIsPublic} onChange={(e) => setEditIsPublic(e.target.checked)} />
                      <span style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>Make this category public</span>
                    </label>
                    {editIsPublic && (
                      <div>
                        <label style={{ color: '#047857', fontSize: '0.85rem', display: 'block', marginBottom: 5 }}>Price (USD) - Leave empty for free</label>
                        <input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="input" placeholder="0.00" min="0" step="0.01" />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                      <button className="btn btn-secondary" onClick={() => setEditingCategory(null)} style={{ flex: 1 }}>Cancel</button>
                      <button className="btn btn-primary" onClick={saveCategory} style={{ flex: 1 }} data-testid="save-category-settings-btn">Save Changes</button>
                    </div>
                    
                    {/* Clean Section */}
                    <div style={{ marginTop: 15, padding: 12, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <p style={{ color: '#f59e0b', fontSize: '0.85rem', margin: 0, fontWeight: 600 }}>
                          <i className="fas fa-broom" style={{ marginRight: 6 }} /> Clean Category Results
                        </p>
                        {categoryResultsCount && (
                          <span style={{ fontSize: '0.75rem', color: '#047857', background: 'rgba(245,158,11,0.2)', padding: '3px 8px', borderRadius: 4 }}>
                            {categoryResultsCount.total_results} results
                          </span>
                        )}
                      </div>
                      {categoryResultsCount && categoryResultsCount.total_results > 0 ? (
                        <>
                          <p style={{ color: '#a1a1aa', fontSize: '0.75rem', margin: '0 0 10px 0' }}>
                            {categoryResultsCount.exclusive_results} exclusive, {categoryResultsCount.shared_results} shared with other categories
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {[
                              { mode: 'remove', icon: 'fa-unlink', label: 'Unlink Results', desc: 'Remove category tag only', color: '#3b82f6', bgColor: '59,130,246' },
                              { mode: 'delete', icon: 'fa-trash', label: 'Delete Exclusive Only', desc: `Delete ${categoryResultsCount.exclusive_results} results only in this category`, color: '#f59e0b', bgColor: '245,158,11' },
                              { mode: 'delete_all', icon: 'fa-bomb', label: 'Delete ALL Results', desc: `Delete all ${categoryResultsCount.total_results} results (cannot be undone!)`, color: '#ef4444', bgColor: '239,68,68' }
                            ].map(action => (
                              <button key={action.mode} onClick={() => cleanCategory(editingCategory.id, action.mode)} disabled={cleaningCategory}
                                style={{
                                  width: '100%', padding: '8px 12px', background: `rgba(${action.bgColor},0.2)`,
                                  border: `1px solid rgba(${action.bgColor},0.4)`, borderRadius: 6, color: action.color,
                                  cursor: cleaningCategory ? 'not-allowed' : 'pointer', fontSize: '0.8rem', textAlign: 'left'
                                }} data-testid={`clean-category-${action.mode}-btn`}>
                                <i className={`fas ${action.icon}`} style={{ marginRight: 6 }} /> <strong>{action.label}</strong>
                                <span style={{ display: 'block', fontSize: '0.7rem', opacity: 0.8, marginTop: 2 }}>{action.desc}</span>
                              </button>
                            ))}
                          </div>
                          {cleaningCategory && <p style={{ color: '#047857', fontSize: '0.75rem', marginTop: 10, textAlign: 'center' }}>Cleaning in progress...</p>}
                        </>
                      ) : (
                        <p style={{ color: '#71717a', fontSize: '0.8rem', margin: 0 }}>No search results in this category to clean.</p>
                      )}
                    </div>
                    
                    {/* Delete Section */}
                    <div style={{ marginTop: 15, padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8 }}>
                      <p style={{ color: '#ef4444', fontSize: '0.8rem', margin: '0 0 10px 0' }}>
                        <i className="fas fa-trash" style={{ marginRight: 6 }} /> Delete this category permanently
                      </p>
                      <button onClick={() => deleteCategory(editingCategory.id)}
                        style={{ width: '100%', padding: '8px 16px', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.5)', borderRadius: 6, color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}
                        data-testid="delete-category-confirm-btn">
                        Delete Category
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Import/Export */}
          {showCategoryManager && categories.length > 0 && (
            <div style={{ marginTop: 20, padding: 15, background: 'rgba(245, 158, 11,0.1)', borderRadius: 10, border: '1px solid rgba(245, 158, 11,0.3)' }}>
              <h4 style={{ color: '#3b82f6', margin: '0 0 15px 0' }}><i className="fas fa-exchange-alt" style={{ marginRight: 6 }} /> Import / Export</h4>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 15 }}>
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API}/category-transfer/export/download`, { headers: { Authorization: `Bearer ${token}` } });
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url;
                      a.download = `infopilot_categories_${new Date().toISOString().split('T')[0]}.json`;
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                      showToast('Categories exported!', 'success');
                    }
                  } catch (e) { showToast('Export failed', 'error'); }
                }} style={{ flex: 1, minWidth: 120, padding: '10px 16px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', border: 'none', borderRadius: 8, color: '#0F172A', cursor: 'pointer', fontWeight: 600 }}
                  data-testid="export-categories-btn">
                  <i className="fas fa-download" style={{ marginRight: 6 }} /> Export JSON
                </button>
                <label style={{ flex: 1, minWidth: 120, padding: '10px 16px', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.5)', borderRadius: 8, color: '#10b981', cursor: 'pointer', fontWeight: 600, textAlign: 'center' }}>
                  <i className="fas fa-upload" style={{ marginRight: 6 }} /> Import JSON
                  <input type="file" accept=".json" style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      try {
                        const content = await file.text(); const data = JSON.parse(content);
                        const result = await post('/category-transfer/import?merge_strategy=merge', { body: data });
                        showToast(result.message || 'Import successful!', 'success'); fetchCategories();
                      } catch (err) { showToast('Invalid JSON file', 'error'); }
                      e.target.value = '';
                    }} data-testid="import-categories-input" />
                </label>
              </div>
              <p style={{ color: '#71717a', fontSize: '0.75rem', margin: 0 }}>
                Export your categories to share or backup. Import will merge with existing categories.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InlineCategoryManager;
